import React, { useCallback, useEffect, useState } from 'react';
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import './ImageLightbox.css';

/**
 * Accessible image lightbox.
 * Props:
 *   open: boolean
 *   slides: [{ src, alt }]
 *   startIndex: number
 *   onIndexChange: (idx) => void  (optional — syncs main image)
 *   onClose: () => void
 */
function ImageLightbox({ open, slides = [], startIndex = 0, onIndexChange, onClose }) {
  const [idx, setIdx] = useState(startIndex);

  useEffect(() => {
    if (open) setIdx(startIndex);
  }, [open, startIndex]);

  const go = useCallback(
    (delta) => {
      setIdx((prev) => {
        const next = (prev + delta + slides.length) % slides.length;
        onIndexChange?.(next);
        return next;
      });
    },
    [slides.length, onIndexChange]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    // Prevent body scroll while open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, go, onClose]);

  if (!open || slides.length === 0) return null;

  const slide = slides[idx];

  return (
    <div className="lightbox-backdrop" role="dialog" aria-modal="true" aria-label="Image viewer" onClick={onClose}>
      <button
        type="button"
        className="lightbox-close"
        onClick={onClose}
        aria-label="Close image viewer"
      >
        <FiX size={24} />
      </button>

      {slides.length > 1 && (
        <button
          type="button"
          className="lightbox-nav lightbox-prev"
          onClick={(e) => {
            e.stopPropagation();
            go(-1);
          }}
          aria-label="Previous image"
        >
          <FiChevronLeft size={28} />
        </button>
      )}

      <figure className="lightbox-figure" onClick={(e) => e.stopPropagation()}>
        <img
          src={slide.src}
          alt={slide.alt || 'Product image'}
          className="lightbox-image"
        />
        <figcaption className="lightbox-caption">
          {idx + 1} / {slides.length}
        </figcaption>
      </figure>

      {slides.length > 1 && (
        <button
          type="button"
          className="lightbox-nav lightbox-next"
          onClick={(e) => {
            e.stopPropagation();
            go(1);
          }}
          aria-label="Next image"
        >
          <FiChevronRight size={28} />
        </button>
      )}
    </div>
  );
}

export default ImageLightbox;
